import type { DocNode, InlineLine } from '@cca/corpus-shared';

/**
 * Helpers for pinning a note's circle to a word. The binding is durable: the
 * chosen word becomes its own run carrying `anchorId === note.id` (ADR-006 —
 * never a word offset), so editing surrounding text can't orphan the anchor.
 */

/** A position within a node: which line + which word. null = end of line. */
export interface AnchorPos {
  li: number;
  wi: number;
}

export const posKey = (p: AnchorPos | null): string => (p ? `${p.li}:${p.wi}` : '');
export const parsePos = (key: string): AnchorPos | null => {
  if (!key) return null;
  const parts = key.split(':');
  const li = Number(parts[0]);
  const wi = Number(parts[1]);
  return Number.isInteger(li) && Number.isInteger(wi) ? { li, wi } : null;
};

const words = (text: string): string[] => text.split(/\s+/).filter(Boolean);

/** Selectable word tokens for a node (label + position key). */
export function nodeTokens(node: DocNode): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  node.lines.forEach((line, li) => {
    const text = line.runs.map((r) => r.text).join('');
    words(text).forEach((w, wi) => out.push({ key: `${li}:${wi}`, label: w }));
  });
  return out;
}

/** Where a note's circle currently sits in a node, or null (end of line). */
export function currentAnchor(node: DocNode, noteId: string): AnchorPos | null {
  for (let li = 0; li < node.lines.length; li++) {
    const runs = node.lines[li]?.runs ?? [];
    let wi = 0;
    for (const run of runs) {
      if (run.anchorId === noteId) return { li, wi };
      wi += words(run.text).length;
    }
  }
  return null;
}

/**
 * Return a new `lines` array with this note's anchor moved to `pos` (or removed
 * when `pos` is null → circle returns to the line end). The target line is
 * rebuilt as up-to-three runs: before · the anchored word · after.
 */
export function setLineAnchor(
  lines: InlineLine[],
  pos: AnchorPos | null,
  noteId: string,
): InlineLine[] {
  // 1. strip any existing binding for this note (drop anchorId/anchorAtWord).
  const cleared: InlineLine[] = lines.map((line) => ({
    ...line,
    runs: line.runs.map((r) =>
      r.anchorId === noteId
        ? { text: r.text, ...(r.bold ? { bold: true } : {}), ...(r.italic ? { italic: true } : {}) }
        : r,
    ),
  }));
  if (!pos) return cleared;

  const line = cleared[pos.li];
  if (!line) return cleared;
  const ws = words(line.runs.map((r) => r.text).join(''));
  if (pos.wi < 0 || pos.wi >= ws.length) return cleared;

  const before = ws.slice(0, pos.wi).join(' ');
  const word = ws[pos.wi] ?? '';
  const after = ws.slice(pos.wi + 1).join(' ');
  const runs = [
    ...(before ? [{ text: `${before} ` }] : []),
    { text: word, anchorId: noteId, anchorAtWord: true },
    ...(after ? [{ text: ` ${after}` }] : []),
  ];
  cleared[pos.li] = { ...line, runs };
  return cleared;
}
